import type { DonationForm, DonationFrequency, DonationTier } from '../models/donation';
import { isObject } from './validations';

export function getSecondsBetween(from: Date, to: Date): number {
  return Math.max(0, Math.floor((to.getTime() - from.getTime()) / 1000))
}

export function getNestedString(obj: unknown, path: readonly string[]): string | null {
  let cur: unknown = obj
  for (const key of path) {
    if (!isObject(cur)) return null
    cur = (cur as Record<string, unknown>)[key]
  }
  return String(cur)
}

export function getStatusColor(status: string): string {
  const normalized = status.trim().toLowerCase()
  if (normalized === 'completed' || normalized === 'complete') return 'green.solid'
  if (normalized === 'pending') return 'orange.solid'
  if (normalized === 'active') return 'green.solid'
  if (
    normalized === 'canceled' ||
    normalized === 'cancelled' ||
    normalized === 'erroring' ||
    normalized === 'error'
  ) {
    return 'red.solid'
  }
  if (normalized === 'inactive') return 'gray.solid'
  return 'gray.solid'
}

export function getAmountTiers(
  formDetails: DonationForm,
  frequency: DonationFrequency,
  options?: { limit?: number }
) {
  const tiers = typeof options?.limit === 'number'
    ? formDetails.donation_tiers.slice(0, options.limit)
    : formDetails.donation_tiers;

  return tiers.map((tier, index) => {
    const tierAmount =
      frequency === 'monthly'
        ? tier.monthly_donation_amount
        : tier.single_donation_amount;
    const numericAmount = parseFloat(tierAmount);
    const displayAmount = Math.round(numericAmount);
    const value = `tier-${index}`;
    const label =
      frequency === 'monthly'
        ? `$${displayAmount}/mo`
        : `$${displayAmount}`;

    return {
      id: tier.id,
      value,
      label,
      numericAmount,
    };
  });
}

export function getCountryOptions() {
  return [
    {value: 'us', label: 'United States'},
    {value: 'ca', label: 'Canada'}
  ];
}

export function clamp01(value: number): number {
  return Math.max(0, Math.min(1, value))
}

export function tiersFromCurrentAmount(amount: number | string): DonationTier[] | null {
  try {
    const numericAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (Number.isNaN(numericAmount) || numericAmount <= 0) {
      return null;
    }

    const multipliers = [1.25, 1.5, 1.75, 2.5] as const;
    const tiers: DonationTier[] = multipliers.map((multiplier, index) => {
      const monthly = Math.ceil(numericAmount * multiplier);
      return {
        id: index + 1,
        tier: index + 1,
        single_donation_amount: String(monthly * 12),
        monthly_donation_amount: String(monthly),
        flag: null,
      };
    });

    // ensure all monthly amount are at least $1 apart, ascending
    tiers.forEach((tier, index) => {
      if (index === 0) return;
      const prev = tiers[index - 1];
      const prevMonthly = parseFloat(prev.monthly_donation_amount);
      const currentMonthly = parseFloat(tier.monthly_donation_amount);
      if (currentMonthly <= prevMonthly) {
        const adjusted = prevMonthly + 1;
        tier.monthly_donation_amount = String(adjusted);
        tier.single_donation_amount = String(adjusted * 12);
      }
    });

    return tiers;
  } catch {
    return null;
  }
}

export function getCAProvinces() {
  return [
    '',
    'AB',
    'BC',
    'MB',
    'NB',
    'NL',
    'NS',
    'NT',
    'NU',
    'ON',
    'PE',
    'QC',
    'SK',
    'YT'
  ];
}

export function getUSStates() {
  return [
    '',
    'AK',
    'AL',
    'AR',
    'AZ',
    'CA',
    'CO',
    'CT',
    'DC',
    'DE',
    'FL',
    'GA',
    'HI',
    'IA',
    'ID',
    'IL',
    'IN',
    'KS',
    'KY',
    'LA',
    'MA',
    'MD',
    'ME',
    'MI',
    'MN',
    'MO',
    'MS',
    'MT',
    'NC',
    'ND',
    'NE',
    'NH',
    'NJ',
    'NM',
    'NV',
    'NY',
    'OH',
    'OK',
    'OR',
    'PA',
    'PR',
    'RI',
    'SC',
    'SD',
    'TN',
    'TX',
    'UT',
    'VA',
    'VT',
    'WA',
    'WI',
    'WV',
    'WY'
  ];
}


